import alert_mixin from '@/utils/mixins/alert_mixin';
import LoadCaseDynamicForm from '@/components/land_court_components/LoadCaseDynamicForm.vue';
import {
  create_land_court_case,
  fetch_land_court_application_forms,
  fetch_land_court_config,
  fetch_land_court_entity_profiles,
  fetch_land_court_entity_types,
  fetch_land_court_form_details,
  fetch_land_court_journals,
  submit_land_court_form_answers,
} from '@/services/land_court_service';
import { fetch_assign_form_status_data } from '@/services/workflow_service';

const LOAD_CASE_MODAL_QUERY = 'load-case';
const CASE_META_STORAGE_KEY = 'land_court_case_meta';
const LAND_COURT_ENTITY_TYPE_STORAGE_KEY = 'land_court_entity_type_id';
const CASELOAD_SNAPSHOT_STORAGE_PREFIX = 'land_court_caseload_snapshot:';
const GOOGLE_MAP_API_KEY = process.env.VUE_APP_GOOGLE_MAP_API_KEY
  || process.env.VUE_APP_GOOGLE_MAPS_API_KEY
  || process.env.VUE_APP_GOOGLE_API_KEY
  || '';
const LAND_COURT_GROUPS_PER_PAGE = 10;
const ENTITY_PROFILE_PAGE_SIZE = 100;
const CASE_ENRICHMENT_BATCH_SIZE = 8;
const SOUTH_AFRICAN_PROVINCES = [
  'Eastern Cape',
  'Free State',
  'Gauteng',
  'KwaZulu-Natal',
  'Limpopo',
  'Mpumalanga',
  'North West',
  'Northern Cape',
  'Western Cape',
];
const SOUTH_AFRICAN_PROVINCE_ALIASES = [
  { province: 'Eastern Cape', aliases: ['eastern cape', 'bhisho'] },
  { province: 'Free State', aliases: ['free state', 'bloemfontein'] },
  { province: 'Gauteng', aliases: ['gauteng', 'johannesburg', 'randburg'] },
  { province: 'KwaZulu-Natal', aliases: ['kwazulu natal', 'kwazulu-natal', 'kzn', 'pietermaritzburg', 'durban'] },
  { province: 'Limpopo', aliases: ['limpopo', 'polokwane'] },
  { province: 'Mpumalanga', aliases: ['mpumalanga', 'mbombela', 'nelspruit'] },
  { province: 'North West', aliases: ['north west', 'mahikeng', 'mafikeng'] },
  { province: 'Northern Cape', aliases: ['northern cape', 'kimberley'] },
  { province: 'Western Cape', aliases: ['western cape', 'cape town'] },
];
const LAND_COURT_CLAIM_TYPES = [
  'Restitution',
  'Governance',
  'Land Court',
  'Labour Tenant',
  'ESTA',
];

const default_case_context = () => ({
  entity_type_id: null,
  entity_type_name: '',
  project_id: null,
  form_ids: [],
  parent_application_id: null,
  parent_entity_profile_id: null,
  parent_entity_type_id: null,
});

const default_filter_form = () => ({
  status: '',
  farm: '',
  province: '',
  legal_representation: '',
  claim_type: '',
  town_district: '',
  municipality: '',
});

function normalize_text(value) {
  return String(value || '').trim();
}

function normalize_label(value) {
  return normalize_text(value).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

function normalize_answer_text(value) {
  if (Array.isArray(value)) {
    return value.map((entry) => normalize_text(entry)).filter(Boolean).join(', ');
  }

  if (value && typeof value === 'object') {
    return normalize_text(
      value.answer_text
      || value.value
      || value.label
      || value.name
      || ''
    );
  }

  return normalize_text(value);
}

function format_date(value) {
  const normalized = normalize_text(value);
  if (!normalized) {
    return '';
  }

  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) {
    return normalized;
  }

  return parsed.toLocaleDateString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function build_status_label(status_label, hearing_date = '') {
  const normalized_status = normalize_text(status_label);
  if (!normalized_status) {
    return '';
  }

  if (!normalize_label(normalized_status).includes('hearing')) {
    return normalized_status;
  }

  const formatted_hearing_date = format_date(hearing_date);
  return formatted_hearing_date ? `${normalized_status} - ${formatted_hearing_date}` : normalized_status;
}

function is_generic_progress_status(status_label) {
  return normalize_label(status_label) === 'in progress';
}

function slugify(value) {
  return normalize_text(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function parse_saved_case_meta() {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const raw = window.sessionStorage.getItem(CASE_META_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function persist_case_meta(case_meta_by_application_id) {
  if (typeof window === 'undefined') {
    return;
  }

  window.sessionStorage.setItem(
    CASE_META_STORAGE_KEY,
    JSON.stringify(case_meta_by_application_id || {})
  );
}

function build_caseload_snapshot_storage_key(case_context = {}, source_page = 1) {
  return `${CASELOAD_SNAPSHOT_STORAGE_PREFIX}${JSON.stringify({
    entity_type_id: Number(case_context?.entity_type_id || 0),
    project_id: Number(case_context?.project_id || 0),
    source_page: Number(source_page || 1),
  })}`;
}

function persist_caseload_snapshot(snapshot_storage_key, snapshot_payload = {}) {
  if (typeof window === 'undefined' || !snapshot_storage_key) {
    return;
  }

  window.sessionStorage.setItem(
    snapshot_storage_key,
    JSON.stringify({
      ...snapshot_payload,
      saved_at: Date.now(),
    })
  );
}

function normalize_application_key(application_id) {
  const normalized = String(application_id || '').trim();
  return /^\d+$/.test(normalized) ? normalized : '';
}

function read_case_meta_entry(case_meta_by_application_id, application_id) {
  const application_key = normalize_application_key(application_id);
  if (!application_key) {
    return {};
  }

  const matching_entry = Object.entries(case_meta_by_application_id || {}).find(
    ([key]) => key === application_key
  );
  return matching_entry?.[1] || {};
}

function write_case_meta_entry(case_meta_by_application_id, application_id, updates = {}) {
  const application_key = normalize_application_key(application_id);
  if (!application_key) {
    return case_meta_by_application_id;
  }

  return {
    ...(case_meta_by_application_id || {}),
    [application_key]: {
      ...read_case_meta_entry(case_meta_by_application_id, application_key),
      ...updates,
    },
  };
}

function non_empty_object_entries(values = {}) {
  return Object.fromEntries(
    Object.entries(values || {}).filter(([, value]) => normalize_text(value))
  );
}

function unique_sorted_values(values = []) {
  return [...new Set(values.map((value) => normalize_text(value)).filter(Boolean))]
    .sort((left, right) => left.localeCompare(right));
}

function representative_text_from_object(value = {}) {
  return normalize_text(
    value.firm_name
    || value.name
    || value.representative_name
    || value.answer_text
    || value.value
    || value.label
    || ''
  );
}

function split_representative_text(value = '') {
  return normalize_text(value).replace(/\r\n/g, '\n');
}

function split_representative_text_blocks(value = '') {
  const normalized = split_representative_text(value);
  if (!normalized) {
    return [];
  }

  return normalized
    .split(/\n\s*\n+/)
    .map((block) => block.trim())
    .filter(Boolean);
}

function split_representative_blocks(value = '') {
  if (Array.isArray(value)) {
    return value.flatMap((entry) => split_representative_blocks(entry));
  }

  if (value && typeof value === 'object') {
    return split_representative_text_blocks(representative_text_from_object(value));
  }

  return split_representative_text_blocks(value);
}

function representative_detail_marker_match(value = '') {
  return normalize_text(value).match(
    /\s+(?:email|e-mail|tel|telephone|phone|contact person|address|ref|representative details)\s*:/i
  );
}

function representative_label_match(value = '') {
  return normalize_text(value).match(
    /(?:^|\n|\s)(?:name|firm name|law firm|representative name)\s*:/i
  );
}

function representative_name_after_label(value = '', label_match = null) {
  const start_index = label_match ? label_match.index + label_match[0].length : 0;
  const remainder = normalize_text(value).slice(start_index);
  const marker_match = representative_detail_marker_match(remainder);
  return normalize_text(marker_match ? remainder.slice(0, marker_match.index) : remainder);
}

function representative_name_from_block(value = '') {
  const normalized = normalize_text(value);
  if (!normalized) {
    return '';
  }

  const label_match = representative_label_match(normalized);
  return label_match
    ? representative_name_after_label(normalized, label_match)
    : representative_name_after_label(normalized);
}

function representative_names_from_values(values = []) {
  return unique_sorted_values(
    values.flatMap((value) =>
      split_representative_blocks(value)
        .map((block) => representative_name_from_block(block))
    )
  );
}

function legal_representative_values_for_case(case_item = {}) {
  return unique_sorted_values([
    ...(Array.isArray(case_item?.legal_representation_options) ? case_item.legal_representation_options : []),
    ...representative_names_from_values([
    case_item?.legal_representation,
    case_item?.legal_representatives,
    case_item?.applicants_legal_representatives,
    case_item?.respondents_legal_representatives,
    ]),
  ]);
}

function is_representative_answer_label(value = '') {
  const label = normalize_label(value);
  return (
    label.includes('legal representative')
    || label.includes('legal representation')
  );
}

function representative_values_from_answer_entries(answer_entries = []) {
  return answer_entries
    .filter((entry) =>
      is_representative_answer_label(entry?.question_text)
      || is_representative_answer_label(entry?.answer_key)
    )
    .map((entry) => entry?.answer_text);
}

function sync_saved_case_statuses(case_meta_by_application_id, pending_cases = []) {
  let has_changes = false;
  const next_case_meta = {
    ...(case_meta_by_application_id || {}),
  };

  pending_cases.forEach((case_item) => {
    const application_id = String(case_item?.application_id || '');
    if (!application_id) {
      return;
    }

    const current_meta = read_case_meta_entry(next_case_meta, application_id);
    const next_status = normalize_text(case_item?.status);
    const next_case_number = normalize_text(case_item?.case_number);
    if (!next_status) {
      return;
    }

    const next_meta = {
      ...(current_meta || {}),
      application_id: Number(application_id),
      case_number: current_meta?.case_number || next_case_number || '',
      case_citation: current_meta?.case_citation || case_item?.case_name || '',
      farm: extract_farm_name(current_meta?.farm || case_item?.group_name || '', next_case_number),
      forum: current_meta?.forum || case_item?.location || '',
      claim_type: current_meta?.claim_type || case_item?.claim_type || '',
      applicants: current_meta?.applicants || case_item?.applicants || '',
      respondents: current_meta?.respondents || case_item?.respondents || '',
      relief_sought: current_meta?.relief_sought || case_item?.relief_sought || '',
      province: current_meta?.province || case_item?.province || '',
      town_district: current_meta?.town_district || case_item?.town_district || '',
      municipality: current_meta?.municipality || case_item?.municipality || '',
      legal_representation: current_meta?.legal_representation || case_item?.legal_representation || '',
      applicants_legal_representatives: current_meta?.applicants_legal_representatives || case_item?.applicants_legal_representatives || '',
      respondents_legal_representatives: current_meta?.respondents_legal_representatives || case_item?.respondents_legal_representatives || '',
      section_16_status: current_meta?.section_16_status || case_item?.section_16_status || '',
      status: next_status,
      hearing_date: current_meta?.hearing_date || case_item?.hearing_date || '',
    };

    const current_serialized = JSON.stringify(current_meta || {});
    const next_serialized = JSON.stringify(next_meta);
    if (current_serialized === next_serialized) {
      return;
    }

    Object.assign(next_case_meta, write_case_meta_entry(next_case_meta, application_id, next_meta));
    has_changes = true;
  });

  return has_changes ? next_case_meta : case_meta_by_application_id;
}

function sort_cases_by_application_desc(case_items = []) {
  return [...case_items].sort(
    (left, right) => Number(right?.sort_order || right?.application_id || right?.entity_profile_id || 0)
      - Number(left?.sort_order || left?.application_id || left?.entity_profile_id || 0)
  );
}

function get_primary_entity_type_name(entity_type) {
  return normalize_text(
    entity_type?.entity_type_name
    || entity_type?.entity_type
    || entity_type?.name
    || entity_type?.label
  );
}

function matches_case_entity_type(entity_type) {
  const name = get_primary_entity_type_name(entity_type).toLowerCase();
  return (
    name.includes('case')
    || name.includes('land court')
    || name.includes('matter')
    || name.includes('claim')
  );
}

function farm_identifier_from_profile(profile = {}) {
  const identifier = normalize_text(profile?.name || profile?.entity_name || profile?.farm_name);
  if (!identifier.includes(' - ')) {
    return identifier;
  }

  const [farm_name, possible_case_suffix] = identifier.rsplit
    ? identifier.rsplit(' - ', 1)
    : (() => {
        const segments = identifier.split(' - ');
        return [segments.slice(0, -1).join(' - '), segments[segments.length - 1]];
      })();

  return /\d/.test(possible_case_suffix || '') ? normalize_text(farm_name) : identifier;
}

function dedupe_farm_identifiers(values = []) {
  const farm_by_identifier = new Map();

  values.forEach((value) => {
    const identifier = normalize_text(value);
    const normalized_identifier = normalize_label(identifier);
    if (!identifier || farm_by_identifier.has(normalized_identifier)) {
      return;
    }

    farm_by_identifier.set(normalized_identifier, identifier);
  });

  return [...farm_by_identifier.values()]
    .sort((left, right) => left.localeCompare(right));
}

function to_number(value) {
  const normalized = Number(value);
  return Number.isFinite(normalized) && normalized > 0 ? normalized : null;
}

function read_stored_land_court_entity_type_id() {
  if (typeof window === 'undefined') {
    return null;
  }

  return to_number(window.sessionStorage.getItem(LAND_COURT_ENTITY_TYPE_STORAGE_KEY));
}

function persist_land_court_entity_type_id(entity_type_id) {
  if (typeof window === 'undefined') {
    return;
  }

  const resolved_id = to_number(entity_type_id);
  if (resolved_id) {
    window.sessionStorage.setItem(LAND_COURT_ENTITY_TYPE_STORAGE_KEY, String(resolved_id));
  } else {
    window.sessionStorage.removeItem(LAND_COURT_ENTITY_TYPE_STORAGE_KEY);
  }
}

function is_active_form_binding(binding = {}) {
  return [
    binding?.is_active,
    binding?.form_is_active,
    binding?.form?.is_active,
  ].some((value) => value === true);
}

function matches_case_filter_value(selected_value, case_value) {
  return !selected_value || case_value === selected_value;
}

function province_values_for_case(case_item = {}) {
  return unique_sorted_values([
    case_item?.province,
    province_from_location(case_item?.location),
    province_from_location(case_item?.case_name),
    province_from_location(case_item?.group_name),
  ]);
}

function matches_case_province_filter(selected_value, case_item = {}) {
  if (!selected_value) {
    return true;
  }

  const selected_label = normalize_label(selected_value);
  return province_values_for_case(case_item)
    .some((province) => normalize_label(province) === selected_label);
}

function matches_case_representative_filter(selected_value, case_item = {}) {
  if (!selected_value) {
    return true;
  }

  const selected_label = normalize_label(selected_value);
  return legal_representative_values_for_case(case_item)
    .some((representative) => normalize_label(representative) === selected_label);
}

function matches_case_text_filter(selected_value, values = []) {
  const selected_label = normalize_label(selected_value);
  if (!selected_label) {
    return true;
  }

  return values.some((value) => normalize_label(value).includes(selected_label));
}

function matches_case_filters(case_item, filter_form = {}) {
  return [
    matches_case_filter_value(filter_form.status, case_item?.status),
    matches_case_filter_value(filter_form.farm, case_item?.group_name),
    matches_case_province_filter(filter_form.province, case_item),
    matches_case_representative_filter(filter_form.legal_representation, case_item),
    matches_case_filter_value(filter_form.claim_type, case_item?.claim_type),
    matches_case_text_filter(filter_form.town_district, [
      case_item?.town_district,
      case_item?.location,
      case_item?.group_name,
    ]),
    matches_case_text_filter(filter_form.municipality, [
      case_item?.municipality,
      case_item?.location,
    ]),
  ].every(Boolean);
}

function case_search_values(case_item = {}) {
  return [
    case_item.case_number,
    case_item.case_name,
    case_item.group_name,
    case_item.location,
    case_item.province,
    case_item.town_district,
    case_item.municipality,
    case_item.applicants,
    case_item.respondents,
  ];
}

function matches_case_search(case_item, normalized_query) {
  if (!normalized_query) {
    return true;
  }

  return case_search_values(case_item)
    .some((value) => String(value || '').toLowerCase().includes(normalized_query));
}

function group_case_items(case_items = []) {
  return case_items.reduce((accumulator, case_item) => {
    const existing_group = accumulator.find((group) => group.id === case_item.group_id);
    if (existing_group) {
      existing_group.cases.push(case_item);
      return accumulator;
    }

    accumulator.push({
      id: case_item.group_id,
      name: case_item.group_name,
      location: case_item.location,
      cases: [case_item],
    });
    return accumulator;
  }, []);
}

function collect_form_questions(form_details = {}) {
  const raw_sections = Array.isArray(form_details?.main_sections)
    ? form_details.main_sections.flatMap((main_section) => main_section?.sub_sections || [])
    : Array.isArray(form_details?.sections)
      ? form_details.sections
      : [];

  return raw_sections
    .flatMap((section) => section?.questions || [])
    .map((question) => ({
      question_id: Number(question?.question_id || question?.id || 0),
      question_text: normalize_text(question?.question_text),
      field_name: normalize_text(
        question?.data_point?.data_point_name
        || question?.question?.data_point?.data_point_name
        || question?.data_point_name
      ),
    }))
    .filter((question) => question.question_id && question.question_text);
}

function province_from_location(location) {
  const normalized = normalize_text(location).toLowerCase();
  const matched_entry = SOUTH_AFRICAN_PROVINCE_ALIASES.find((entry) =>
    entry.aliases.some((alias) => normalized.includes(alias))
  );

  return matched_entry?.province || '';
}

function extract_case_number_from_name(value) {
  const normalized = normalize_text(value);
  if (!normalized) {
    return '';
  }

  const segments = normalized.split(' - ').map((segment) => normalize_text(segment)).filter(Boolean);
  if (segments.length > 1) {
    return segments[segments.length - 1];
  }

  return '';
}

function extract_farm_name(value, case_number = '') {
  const normalized = normalize_text(value);
  const normalized_case_number = normalize_text(case_number);
  if (!normalized) {
    return '';
  }

  if (normalized_case_number) {
    const suffix = ` - ${normalized_case_number}`;
    if (normalized.endsWith(suffix)) {
      return normalize_text(normalized.slice(0, -suffix.length));
    }
  }

  return normalized;
}

function build_unknown_farm_label(application_id = '') {
  const normalized_application_id = Number(application_id || 0);
  return normalized_application_id ? `Farm ${normalized_application_id}` : 'Unknown farm';
}

async function run_in_batches(items = [], batch_size = CASE_ENRICHMENT_BATCH_SIZE, task = async () => null) {
  const results = [];
  for (let index = 0; index < items.length; index += batch_size) {
    const batch = items.slice(index, index + batch_size);
    const batch_results = await Promise.all(batch.map(task));
    results.push(...batch_results);
  }

  return results;
}

function handle_background_enrichment_error(error) {
  if (process.env.NODE_ENV !== 'production') {
    console.debug('Land court background enrichment failed:', error);
  }
}

const LandCourtHome = {
  name: 'LandCourtHome',
  mixins: [alert_mixin],
  components: {
    LoadCaseDynamicForm,
  },
  data() {
    return {
      loading_caseload: false,
      create_case_running: false,
      show_load_case_modal: false,
      show_filters: false,
      submit_case_error: '',
      search_query: '',
      current_page: 1,
      source_page: 1,
      source_total_pages: 1,
      source_total_count: 0,
      source_batch_count: 0,
      open_group_ids: [],
      filter_form: default_filter_form(),
      available_entity_types: [],
      selected_entity_type_id: null,
      case_context: default_case_context(),
      initial_workflow_status_label: '',
      tracked_question_ids: [],
      tracked_field_names: [],
      tracked_question_labels: {},
      case_answer_field_labels: {},
      representative_catalog_options: [],
      farm_catalog_options: [],
      pending_cases: [],
      case_meta_by_application_id: parse_saved_case_meta(),
      caseload_loader_close_handler: null,
      representative_catalog_loading: false,
      town_district_autocomplete: null,
      town_district_input: null,
    };
  },
  computed: {
    entity_type_options() {
      const preferred_types = this.available_entity_types.filter(matches_case_entity_type);
      return preferred_types.length ? preferred_types : this.available_entity_types;
    },
    selected_entity_type() {
      return this.entity_type_options.find((item) => Number(item?.id) === Number(this.selected_entity_type_id)) || null;
    },
    filter_options() {
      const saved_meta_values = Object.values(this.case_meta_by_application_id || {});
      return {
        statuses: unique_sorted_values(this.pending_cases.map((case_item) => case_item.status)),
        farms: unique_sorted_values(this.pending_cases.map((case_item) => case_item.group_name)),
        provinces: unique_sorted_values([
          ...SOUTH_AFRICAN_PROVINCES,
          ...this.pending_cases.map((case_item) => case_item.province),
        ]),
        legal_representations: unique_sorted_values(
          [
            ...this.representative_catalog_options,
            ...this.pending_cases.flatMap((case_item) => legal_representative_values_for_case(case_item)),
            ...saved_meta_values.map((meta) => meta?.legal_representation),
            ...saved_meta_values.map((meta) => meta?.applicants_legal_representatives),
            ...saved_meta_values.map((meta) => meta?.respondents_legal_representatives),
          ]
        ),
        claim_types: LAND_COURT_CLAIM_TYPES,
      };
    },
    load_case_data_selection_options() {
      const saved_meta_values = Object.values(this.case_meta_by_application_id || {});
      const options = {
        farms: dedupe_farm_identifiers(this.farm_catalog_options),
        representatives: representative_names_from_values([
          ...this.representative_catalog_options,
          ...this.pending_cases.flatMap((case_item) => legal_representative_values_for_case(case_item)),
          ...saved_meta_values.map((meta) => meta?.legal_representation),
          ...saved_meta_values.map((meta) => meta?.applicants_legal_representatives),
          ...saved_meta_values.map((meta) => meta?.respondents_legal_representatives),
        ]),
      };

      return options;
    },
    active_filter_count() {
      return Object.values(this.filter_form).filter((value) => normalize_text(value)).length;
    },
    active_filter_summary() {
      if (!this.active_filter_count) {
        return 'No filters applied';
      }

      return `${this.active_filter_count} filter${this.active_filter_count === 1 ? '' : 's'} applied`;
    },
    total_cases() {
      return this.pending_cases.length;
    },
    filtered_case_items() {
      const normalized_query = normalize_text(this.search_query).toLowerCase();
      return this.pending_cases.filter(
        (case_item) => matches_case_filters(case_item, this.filter_form) && matches_case_search(case_item, normalized_query)
      );
    },
    filtered_case_groups() {
      return group_case_items(this.filtered_case_items);
    },
    total_group_pages() {
      return Math.max(1, Math.ceil(this.filtered_case_items.length / LAND_COURT_GROUPS_PER_PAGE));
    },
    paginated_case_groups() {
      const start_index = (this.current_page - 1) * LAND_COURT_GROUPS_PER_PAGE;
      const paginated_items = this.filtered_case_items.slice(start_index, start_index + LAND_COURT_GROUPS_PER_PAGE);
      return group_case_items(paginated_items);
    },
    batch_entries_total() {
      return Number(this.source_batch_count || this.filtered_case_items.length || 0);
    },
    page_group_range() {
      if (!this.batch_entries_total) {
        return {
          start: 0,
          end: 0,
        };
      }

      const start = ((this.current_page - 1) * LAND_COURT_GROUPS_PER_PAGE) + 1;
      const end = Math.min(this.current_page * LAND_COURT_GROUPS_PER_PAGE, this.batch_entries_total);

      return { start, end };
    },
    source_page_summary() {
      return `Showing page ${this.source_page} of ${this.source_total_pages}`;
    },
    batch_entries_summary() {
      if (!this.batch_entries_total) {
        return 'Showing 0 to 0 of 0 entries';
      }

      return `Showing ${this.page_group_range.start} to ${this.page_group_range.end} of ${this.batch_entries_total} entries`;
    },
    visible_page_numbers() {
      const total_pages = this.total_group_pages;
      if (total_pages <= 5) {
        return Array.from({ length: total_pages }, (_, index) => index + 1);
      }

      const start_page = Math.max(1, Math.min(this.current_page - 2, total_pages - 4));
      return Array.from({ length: 5 }, (_, index) => start_page + index);
    },
    summary_text() {
      return `${this.total_cases} case${this.total_cases === 1 ? '' : 's'} across ${this.filtered_case_groups.length} farm${this.filtered_case_groups.length === 1 ? '' : 's'}`;
    },
  },
  watch: {
    '$route.query.open_modal': {
      immediate: true,
      handler(value) {
        if (value === LOAD_CASE_MODAL_QUERY) {
          this.show_load_case_modal = true;
        }
      }
    },
    search_query() {
      this.current_page = 1;
    },
    filter_form: {
      deep: true,
      handler() {
        this.current_page = 1;
      }
    },
    filtered_case_items() {
      if (this.current_page > this.total_group_pages) {
        this.current_page = this.total_group_pages;
      }
    },
    filtered_case_groups(groups = []) {
      if (groups.length) {
        this.dismiss_visible_caseload_loader();
      }
    }
  },
  async created() {
    await this.initialize_page();
  },
  mounted() {
    this.$nextTick(() => {
      this.load_google_map_event();
    });
  },
  methods: {
    apply_caseload_snapshot(snapshot = {}) {
      this.case_answer_field_labels = snapshot?.case_answer_field_labels || {};
      this.source_total_pages = Number(snapshot?.source_total_pages || 1) || 1;
      this.source_total_count = Number(snapshot?.source_total_count || 0) || 0;
      this.source_batch_count = Number(snapshot?.source_batch_count || 0) || 0;
      this.pending_cases = Array.isArray(snapshot?.pending_cases) ? snapshot.pending_cases : [];
      this.open_group_ids = Array.isArray(snapshot?.open_group_ids)
        ? snapshot.open_group_ids
        : [...new Set(this.pending_cases.map((item) => item.group_id))];
      this.$store.dispatch('setLandCourtCaseCount', this.pending_cases.length);
    },

    async initialize_page() {
      this.strip_entity_type_query_params();
      const loader = await this.show_loader('Loading caseload...', 0);
      let loader_closed = false;
      const close_loader_once = async () => {
        if (loader_closed) {
          return;
        }

        loader_closed = true;
        this.caseload_loader_close_handler = null;
        await this.close_loader(loader);
      };
      this.caseload_loader_close_handler = close_loader_once;

      try {
        await this.load_available_entity_types();
        await this.refresh_case_context_for_selected_entity_type(
          this.selected_entity_type_id,
          { close_loader: close_loader_once }
        );
      } catch (error) {
        const message = error?.response?.data?.message || error?.message || 'Failed to load the land court caseload.';
        this.show_error(message);
      } finally {
        await close_loader_once();
      }
    },

    async load_available_entity_types() {
      const entity_types = await fetch_land_court_entity_types();
      this.available_entity_types = Array.isArray(entity_types) ? entity_types : [];

      const stored_entity_type_id = read_stored_land_court_entity_type_id();
      const selectable_entity_types = this.entity_type_options;
      const preferred_entity_type = stored_entity_type_id
        ? selectable_entity_types.find((item) => Number(item?.id) === stored_entity_type_id)
        : selectable_entity_types.find(matches_case_entity_type) || selectable_entity_types[0];

      this.selected_entity_type_id = to_number(preferred_entity_type?.id);
    },

    async refresh_case_context_for_selected_entity_type(entity_type_id, options = {}) {
      const resolved_entity_type_id = to_number(entity_type_id);
      const preferred_entity_type = this.entity_type_options.find(
        (item) => Number(item?.id) === resolved_entity_type_id
      );

      if (!resolved_entity_type_id || !preferred_entity_type) {
        this.case_context = default_case_context();
        this.pending_cases = [];
        this.open_group_ids = [];
        this.representative_catalog_options = [];
        this.farm_catalog_options = [];
        throw new Error('No backend entity type is configured for land court cases yet.');
      }

      const config_data = await fetch_land_court_config(resolved_entity_type_id).catch(() => ({}));
      const form_bindings = Array.isArray(config_data?.form_bindings) ? config_data.form_bindings : [];
      const form_ids = form_bindings
        .filter(is_active_form_binding)
        .map((binding) => Number(binding?.form_id || binding?.id || 0))
        .filter((id) => id > 0);

      this.case_context = {
        entity_type_id: resolved_entity_type_id,
        entity_type_name: get_primary_entity_type_name(preferred_entity_type),
        project_id: to_number(
          this.$route?.query?.project_id
          || preferred_entity_type?.project_id
          || preferred_entity_type?.project?.id
          || preferred_entity_type?.sub_program?.project_id
          || preferred_entity_type?.sub_program?.project?.id
          || config_data?.entity_config?.project_id
        ),
        form_ids: [...new Set(form_ids)],
        parent_application_id: null,
        parent_entity_profile_id: null,
        parent_entity_type_id: null,
      };

      this.source_page = 1;
      this.current_page = 1;
      this.representative_catalog_options = [];
      this.farm_catalog_options = [];
      persist_land_court_entity_type_id(resolved_entity_type_id);
      await this.load_caseload(options);
    },

    async load_caseload(options = {}) {
      this.loading_caseload = true;
      const snapshot_storage_key = build_caseload_snapshot_storage_key(
        this.case_context,
        this.source_page
      );

      try {
        await this.hydrate_initial_workflow_status_label();
        await this.hydrate_case_question_catalog();
        const response = await this.fetch_case_profile_batch();

        this.case_answer_field_labels = response?.field_labels || {};
        this.source_total_pages = Number(response?.pagination?.total_pages || 1) || 1;
        this.source_total_count = Number(response?.pagination?.total_count || 0) || 0;
        this.source_batch_count = Array.isArray(response?.data) ? response.data.length : 0;

        this.update_caseload_from_profile_records(response?.data);
        const synced_case_meta = sync_saved_case_statuses(
          this.case_meta_by_application_id,
          this.pending_cases
        );
        if (synced_case_meta !== this.case_meta_by_application_id) {
          this.case_meta_by_application_id = synced_case_meta;
          persist_case_meta(this.case_meta_by_application_id);
          this.update_caseload_from_profile_records(response?.data);
        }

        this.loading_caseload = false;
        if (typeof options.close_loader === 'function') {
          await options.close_loader();
        }

        this.hydrate_case_answer_meta(this.pending_cases)
          .then(() => {
            this.update_caseload_from_profile_records(response?.data);
          })
          .catch(handle_background_enrichment_error);
        this.hydrate_representative_catalog_from_records(response?.data);
        this.hydrate_filter_catalogs().catch(handle_background_enrichment_error);
        this.hydrate_filing_counts().catch(handle_background_enrichment_error);
        this.continue_loading_caseload_in_background(this.source_page + 1);
        persist_caseload_snapshot(snapshot_storage_key, {
          case_answer_field_labels: this.case_answer_field_labels,
          source_total_pages: this.source_total_pages,
          source_total_count: this.source_total_count,
          source_batch_count: this.source_batch_count,
          pending_cases: this.pending_cases,
          open_group_ids: this.open_group_ids,
        });
      } finally {
        if (typeof options.close_loader === 'function') {
          await options.close_loader();
        }
        this.loading_caseload = false;
      }
    },

    dismiss_visible_caseload_loader() {
      if (!this.caseload_loader_close_handler) {
        return;
      }

      this.loading_caseload = false;
      this.$nextTick(() => {
        if (this.caseload_loader_close_handler) {
          this.caseload_loader_close_handler();
        }
      });
    },

    async hydrate_farm_catalog() {
      const farm_records = await this.fetch_farm_profile_records();
      this.farm_catalog_options = dedupe_farm_identifiers(
        farm_records.map(farm_identifier_from_profile)
      );
    },

    async hydrate_filter_catalogs() {
      await Promise.all([
        this.hydrate_farm_catalog(),
        this.hydrate_representative_catalog(),
      ]);
    },

    async fetch_farm_profile_records() {
      const project_farm_records = await this.fetch_profile_catalog_records({ farm_only: true });

      if (project_farm_records.length) {
        return project_farm_records;
      }

      return this.fetch_profile_catalog_records(
        { farm_only: true },
        { include_project_filter: false }
      );
    },

    async fetch_profile_catalog_records(extra_params = {}, options = {}) {
      const include_project_filter = options.include_project_filter !== false;
      const first_response = await fetch_land_court_entity_profiles({
        ...extra_params,
        page_size: ENTITY_PROFILE_PAGE_SIZE,
        page: 1,
        ...(include_project_filter && this.case_context.project_id ? { project_id: this.case_context.project_id } : {}),
      });

      const total_pages = Math.max(1, Number(first_response?.pagination?.total_pages || 1));
      const remaining_pages = Array.from({ length: Math.max(total_pages - 1, 0) }, (_, index) => index + 2);
      const remaining_responses = await Promise.all(
        remaining_pages.map((page) =>
          fetch_land_court_entity_profiles({
            ...extra_params,
            page_size: ENTITY_PROFILE_PAGE_SIZE,
            page,
            ...(include_project_filter && this.case_context.project_id ? { project_id: this.case_context.project_id } : {}),
          }).catch(() => null)
        )
      );

      return [
        ...(first_response?.data || []),
        ...remaining_responses
          .filter(Boolean)
          .flatMap((response) => response?.data || []),
      ];
    },

    async hydrate_case_question_catalog() {
      const form_ids = [...new Set((this.case_context.form_ids || []).map((form_id) => Number(form_id)).filter(Boolean))];

      if (!form_ids.length) {
        this.tracked_question_ids = [];
        this.tracked_field_names = [];
        this.tracked_question_labels = {};
        return;
      }

      const form_details_list = await Promise.all(
        form_ids.map((form_id) => fetch_land_court_form_details(form_id).catch(() => null))
      );

      const question_entries = form_details_list
        .filter(Boolean)
        .flatMap((form_details) => collect_form_questions(form_details));

      const tracked_question_labels = {};
      const tracked_field_names = [];
      question_entries.forEach((question) => {
        if (question.question_id) {
          tracked_question_labels[String(question.question_id)] = question.question_text;
        }
        if (question.section_question_id) {
          tracked_question_labels[String(question.section_question_id)] = question.question_text;
        }
        if (question.field_name) {
          tracked_field_names.push(question.field_name);
        }
      });

      const questions_without_field_names = question_entries
        .filter((question) => !question.field_name)
        .map((question) => question.question_id)
        .filter(Boolean);

      this.tracked_question_ids = [...new Set(questions_without_field_names)];
      this.tracked_field_names = [...new Set(tracked_field_names)];
      this.tracked_question_labels = tracked_question_labels;
    },

    async fetch_case_profile_batch(page_number = this.source_page) {
      return fetch_land_court_entity_profiles({
        entity_type_id: this.case_context.entity_type_id,
        page_size: ENTITY_PROFILE_PAGE_SIZE,
        page: page_number,
        ...(this.case_context.project_id ? { project_id: this.case_context.project_id } : {}),
        ...(this.filter_form.town_district ? { town_district: this.filter_form.town_district } : {}),
        ...(this.filter_form.municipality ? { municipality: this.filter_form.municipality } : {}),
        ...(this.tracked_question_ids.length ? { question_ids: this.tracked_question_ids } : {}),
        ...(this.tracked_field_names.length ? { fields: this.tracked_field_names } : {}),
      });
    },

    update_caseload_from_profile_records(profile_records = [], append_records = false) {
      const backend_cases = (Array.isArray(profile_records) ? profile_records : [])
        .map((case_record) => this.map_case_record(case_record))
        .filter(Boolean);
      const case_items = append_records
        ? [...this.pending_cases, ...backend_cases]
        : backend_cases;

      this.pending_cases = this.merge_cases_with_local_meta(case_items);
      this.$store.dispatch('setLandCourtCaseCount', this.pending_cases.length);
      this.open_group_ids = [...new Set(this.pending_cases.map((item) => item.group_id))];
    },

    async continue_loading_caseload_in_background(start_page = 2) {
      const total_pages = Math.max(1, Number(this.source_total_pages || 1));
      const first_background_page = Math.max(2, Number(start_page || 2));

      for (let page = first_background_page; page <= total_pages; page += 1) {
        try {
          const response = await this.fetch_case_profile_batch(page);
          this.case_answer_field_labels = {
            ...(this.case_answer_field_labels || {}),
            ...(response?.field_labels || {}),
          };
          this.update_caseload_from_profile_records(response?.data, true);
          await this.hydrate_case_answer_meta(this.pending_cases);
          this.hydrate_representative_catalog_from_records(response?.data);
          await this.hydrate_filing_counts();
        } catch {
          // Background loading should not block the visible caseload.
        }
      }
    },

    async hydrate_representative_catalog() {
      if (this.representative_catalog_loading) {
        return;
      }

      this.representative_catalog_loading = true;

      try {
        const extra_params = {
          entity_type_id: this.case_context.entity_type_id,
          ...(this.tracked_question_ids.length ? { question_ids: this.tracked_question_ids } : {}),
          ...(this.tracked_field_names.length ? { fields: this.tracked_field_names } : {}),
        };
        let catalog_records = await this.fetch_profile_catalog_records(extra_params);

        if (!catalog_records.length) {
          catalog_records = await this.fetch_profile_catalog_records(
            extra_params,
            { include_project_filter: false }
          );
        }

        this.hydrate_representative_catalog_from_records(catalog_records);
      } finally {
        this.representative_catalog_loading = false;
      }
    },

    collect_representative_values_from_records(profile_records = []) {
      return (Array.isArray(profile_records) ? profile_records : [])
        .flatMap((case_record) => {
          const answer_entries = this.build_answer_entries_from_profile(case_record);
          const extracted_meta = this.extract_case_meta_from_answers(answer_entries);
          const saved_case_meta_entry = this.find_saved_case_meta_entry(
            Number(case_record?.entity_profile_id || case_record?.id || 0),
            Number(case_record?.application_id || case_record?.latest_application_id || case_record?.current_application_id || 0)
          );

          return this.collect_representative_values_from_sources({
            answer_entries,
            extracted_meta,
            saved_meta: saved_case_meta_entry?.meta || {},
            case_record,
          });
        });
    },

    hydrate_representative_catalog_from_records(profile_records = []) {
      const representative_values = this.collect_representative_values_from_records(profile_records);
      this.representative_catalog_options = representative_names_from_values([
        ...this.representative_catalog_options,
        ...representative_values,
      ]);
    },

    async hydrate_initial_workflow_status_label() {
      this.initial_workflow_status_label = '';

      const form_id = Number((this.case_context.form_ids || [])[0] || 0);
      if (!form_id) {
        return;
      }

      try {
        const workflow_status_response = await fetch_assign_form_status_data({ form_id });
        const assigned_statuses = workflow_status_response?.data?.assigned_statuses
          || workflow_status_response?.assigned_statuses
          || [];
        const initial_workflow_status = assigned_statuses.find((status) => Boolean(status?.is_initial))
          || assigned_statuses[0]
          || null;

        this.initial_workflow_status_label = normalize_text(initial_workflow_status?.status_name);
      } catch {
        this.initial_workflow_status_label = '';
      }
    },

    async hydrate_filing_counts() {
      const count_entries = await run_in_batches(
        this.pending_cases,
        CASE_ENRICHMENT_BATCH_SIZE,
        async (case_item) => {
          const application_id = Number(case_item?.application_id || 0);
          if (!application_id) {
            return null;
          }

          try {
            const journals = await fetch_land_court_journals(application_id);
            return {
              application_id,
              filings: Array.isArray(journals) ? journals.length : 0,
            };
          } catch {
            return null;
          }
        }
      );

      const filings_by_application_id = new Map(
        count_entries
          .filter(Boolean)
          .map((entry) => [Number(entry.application_id), Number(entry.filings || 0)])
      );

      if (!filings_by_application_id.size) {
        return;
      }

      let has_case_changes = false;
      this.pending_cases = this.pending_cases.map((case_item) => {
        const application_id = Number(case_item?.application_id || 0);
        if (!filings_by_application_id.has(application_id)) {
          return case_item;
        }

        const next_filings = filings_by_application_id.get(application_id);
        if (Number(case_item?.filings || 0) === next_filings) {
          return case_item;
        }

        has_case_changes = true;
        return {
          ...case_item,
          filings: next_filings,
        };
      });

      let has_meta_changes = false;
      const next_case_meta = {
        ...(this.case_meta_by_application_id || {}),
      };

      filings_by_application_id.forEach((filings, application_id) => {
        const key = String(application_id);
        const current_meta = next_case_meta[key] || {};
        if (Number(current_meta?.filings || 0) === Number(filings || 0)) {
          return;
        }

        next_case_meta[key] = {
          ...current_meta,
          application_id,
          filings: Number(filings || 0),
        };
        has_meta_changes = true;
      });

      if (has_meta_changes) {
        this.case_meta_by_application_id = next_case_meta;
        persist_case_meta(this.case_meta_by_application_id);
      }

      if (has_case_changes) {
        this.open_group_ids = [...new Set(this.pending_cases.map((item) => item.group_id))];
      }
    },

    map_case_record(case_record = {}) {
      const entity_profile_id = Number(case_record?.entity_profile_id || case_record?.id || 0);
      const explicit_application_id = Number(
        case_record?.application_id
        || case_record?.latest_application_id
        || case_record?.current_application_id
        || 0
      );
      const saved_case_meta_entry = this.find_saved_case_meta_entry(entity_profile_id, explicit_application_id);
      const saved_meta = saved_case_meta_entry?.meta || {};
      const application_id = explicit_application_id || Number(saved_meta?.application_id || 0);
      const record_id = entity_profile_id || application_id;
      if (!record_id) {
        return null;
      }

      const answer_entries = this.build_answer_entries_from_profile(case_record);
      const extracted_case_meta = this.extract_case_meta_from_answers(answer_entries);
      const entity_name = this.first_non_empty_value([
        case_record?.name,
        case_record?.entity_name,
        case_record?.current_entity_name,
      ]);
      const case_number = this.first_non_empty_value([
        saved_meta.case_number,
        extracted_case_meta.case_number,
        extract_case_number_from_name(entity_name),
        application_id ? `APP ${application_id}` : `PROFILE ${record_id}`,
      ]);
      const group_name = this.first_non_empty_value([
        extract_farm_name(saved_meta.farm, case_number),
        extract_farm_name(extracted_case_meta.farm, case_number),
        extract_farm_name(entity_name, case_number),
        entity_name,
        application_id ? `Case group ${application_id}` : `Case group ${record_id}`,
      ]);
      const case_name = this.first_non_empty_value([
        saved_meta.case_citation,
        extracted_case_meta.case_citation,
        entity_name,
        application_id ? `Loaded case ${application_id}` : `Loaded case ${record_id}`,
      ]);
      const location = this.first_non_empty_value([
        saved_meta.forum,
        extracted_case_meta.forum,
        this.case_context.entity_type_name || 'Land Court',
      ]);
      const town_district = this.first_non_empty_value([
        saved_meta.town_district,
        extracted_case_meta.town_district,
        case_record?.town_district,
      ]);
      const municipality = this.first_non_empty_value([
        saved_meta.municipality,
        extracted_case_meta.municipality,
        case_record?.municipality,
      ]);
      const backend_status = this.resolve_backend_case_status(case_record);
      const status = this.resolve_case_status_label(backend_status, saved_meta.status);
      const hearing_date = this.first_non_empty_value([
        saved_meta.hearing_date,
        case_record?.hearing_date,
      ]);
      const representative_values = representative_names_from_values(
        this.collect_representative_values_from_sources({
          answer_entries,
          extracted_meta: extracted_case_meta,
          saved_meta,
          case_record,
        })
      );
      const applicants_legal_representatives = this.first_non_empty_value([
        saved_meta.applicants_legal_representatives,
        extracted_case_meta.applicants_legal_representatives,
        case_record?.applicants_legal_representatives,
      ]);
      const respondents_legal_representatives = this.first_non_empty_value([
        saved_meta.respondents_legal_representatives,
        extracted_case_meta.respondents_legal_representatives,
        extracted_case_meta.legal_representation,
        case_record?.respondents_legal_representatives,
        case_record?.legal_representation,
      ]);
      const legal_representation_options = representative_names_from_values([
        ...representative_values,
        applicants_legal_representatives,
        respondents_legal_representatives,
        saved_meta.legal_representation,
        case_record?.legal_representation,
      ]);

      return {
        application_id,
        entity_profile_id: entity_profile_id || Number(saved_meta?.entity_profile_id || 0) || null,
        backend_status,
        backend_status_id: Number(case_record?.application_status_id || case_record?.status?.id || 0) || null,
        saved_status: saved_meta.status || '',
        record_id,
        merge_key: application_id ? `application-${application_id}` : `profile-${record_id}`,
        sort_order: application_id || record_id,
        group_id: slugify(group_name) || `group-${record_id}`,
        group_name,
        location,
        case_number,
        case_name,
        status: build_status_label(status, hearing_date),
        status_variant: this.status_variant_from_status(status),
        filings: Number(saved_meta.filings || 0),
        applicants: saved_meta.applicants || extracted_case_meta.applicants || '',
        respondents: saved_meta.respondents || extracted_case_meta.respondents || '',
        applicants_legal_representatives,
        respondents_legal_representatives,
        legal_representatives: legal_representation_options.join('\n\n'),
        legal_representation_options,
        relief_sought: saved_meta.relief_sought || extracted_case_meta.relief_sought || '',
        claim_type: saved_meta.claim_type || extracted_case_meta.claim_type || '',
        town_district,
        municipality,
        province: this.first_non_empty_value([
          saved_meta.province,
          extracted_case_meta.province,
          province_from_location(location),
        ]),
        legal_representation: this.first_non_empty_value([
          saved_meta.legal_representation,
          respondents_legal_representatives,
          applicants_legal_representatives,
          legal_representation_options.join('\n\n'),
        ]),
        section_16_status: saved_meta.section_16_status || extracted_case_meta.section_16_status || '',
        hearing_date,
      };
    },

    build_case_item_from_saved_meta(application_id, saved_meta = {}) {
      const case_number = this.first_non_empty_value([
        saved_meta.case_number,
        extract_case_number_from_name(saved_meta.entity_name),
        `APP ${application_id}`,
      ]);
      const group_name = this.first_non_empty_value([
        extract_farm_name(saved_meta.farm, case_number),
        build_unknown_farm_label(application_id),
      ]);
      const case_name = this.first_non_empty_value([
        saved_meta.case_citation,
        build_party_case_name(saved_meta.applicants, saved_meta.respondents),
        saved_meta.entity_name,
        `Loaded case ${application_id}`,
      ]);
      const location = this.first_non_empty_value([
        saved_meta.forum,
        this.case_context.entity_type_name,
        'Land Court',
      ]);
      const town_district = this.first_non_empty_value([
        saved_meta.town_district,
      ]);
      const municipality = this.first_non_empty_value([
        saved_meta.municipality,
      ]);
      const hearing_date = this.first_non_empty_value([
        saved_meta.hearing_date,
      ]);
      const status = this.resolve_case_status_label('', saved_meta.status);
      const legal_representation_options = representative_names_from_values([
        saved_meta.legal_representation,
        saved_meta.applicants_legal_representatives,
        saved_meta.respondents_legal_representatives,
      ]);

      return {
        application_id,
        entity_profile_id: Number(saved_meta?.entity_profile_id || 0) || null,
        backend_status: '',
        backend_status_id: null,
        saved_status: saved_meta.status || '',
        record_id: Number(saved_meta?.entity_profile_id || application_id || 0),
        merge_key: application_id ? `application-${application_id}` : `profile-${saved_meta?.entity_profile_id || 'saved'}`,
        sort_order: Number(application_id || saved_meta?.entity_profile_id || 0),
        group_id: slugify(group_name) || `group-${application_id}`,
        group_name,
        location,
        case_number,
        case_name,
        status: build_status_label(status, hearing_date),
        status_variant: this.status_variant_from_status(status),
        filings: Number(saved_meta.filings || 0),
        applicants: saved_meta.applicants || '',
        respondents: saved_meta.respondents || '',
        applicants_legal_representatives: saved_meta.applicants_legal_representatives || '',
        respondents_legal_representatives: saved_meta.respondents_legal_representatives || '',
        legal_representatives: legal_representation_options.join('\n\n'),
        legal_representation_options,
        relief_sought: saved_meta.relief_sought || '',
        claim_type: saved_meta.claim_type || '',
        town_district,
        municipality,
        province: this.first_non_empty_value([
          saved_meta.province,
          province_from_location(location),
        ]),
        legal_representation: this.first_non_empty_value([
          saved_meta.legal_representation,
          saved_meta.respondents_legal_representatives,
          saved_meta.applicants_legal_representatives,
        ]),
        section_16_status: saved_meta.section_16_status || '',
        hearing_date,
      };
    },

    get_local_cases_for_selected_entity_type() {
      const selected_entity_type_id = Number(this.case_context.entity_type_id || 0);

      return Object.entries(this.case_meta_by_application_id || {})
        .map(([application_id, saved_meta]) => ({
          application_id: Number(application_id),
          saved_meta,
        }))
        .filter(({ application_id, saved_meta }) => {
          if (!application_id) {
            return false;
          }

          const meta_entity_type_id = Number(saved_meta?.entity_type_id || 0);
          return !selected_entity_type_id || !meta_entity_type_id || meta_entity_type_id === selected_entity_type_id;
        })
        .map(({ application_id, saved_meta }) => this.build_case_item_from_saved_meta(application_id, saved_meta))
        .filter(Boolean);
    },

    merge_cases_with_local_meta(backend_cases = []) {
      const merged_case_items = new Map();

      backend_cases.forEach((case_item) => {
        if (case_item?.merge_key) {
          merged_case_items.set(case_item.merge_key, case_item);
        }
      });

      this.get_local_cases_for_selected_entity_type().forEach((case_item) => {
        if (case_item?.merge_key && !merged_case_items.has(case_item.merge_key)) {
          merged_case_items.set(case_item.merge_key, case_item);
        }
      });

      return sort_cases_by_application_desc(Array.from(merged_case_items.values()));
    },

    find_saved_case_meta_entry(entity_profile_id, application_id) {
      const normalized_entity_profile_id = Number(entity_profile_id || 0);
      const normalized_application_id = Number(application_id || 0);

      if (normalized_application_id) {
        const direct_match = this.case_meta_by_application_id[String(normalized_application_id)];
        if (direct_match) {
          return {
            key: String(normalized_application_id),
            meta: direct_match,
          };
        }
      }

      if (!normalized_entity_profile_id) {
        return null;
      }

      const matching_entry = Object.entries(this.case_meta_by_application_id || {}).find(([, meta]) =>
        Number(meta?.entity_profile_id || 0) === normalized_entity_profile_id
      );

      if (!matching_entry) {
        return null;
      }

      return {
        key: matching_entry[0],
        meta: matching_entry[1] || {},
      };
    },

    build_answer_entries_from_profile(case_record = {}) {
      const answers = case_record?.answers && typeof case_record.answers === 'object'
        ? case_record.answers
        : {};
      const merged_labels = {
        ...(this.tracked_question_labels || {}),
        ...(this.case_answer_field_labels || {}),
      };

      return Object.entries(answers).map(([question_id, answer_value]) => ({
        question_id: Number(question_id || 0) || null,
        question_text: normalize_text(merged_labels[String(question_id)] || ''),
        answer_key: normalize_text(question_id),
        answer_text: normalize_answer_text(answer_value),
      }));
    },

    collect_representative_values_from_sources({
      answer_entries = [],
      extracted_meta = {},
      saved_meta = {},
      case_record = {},
    } = {}) {
      return [
        ...representative_values_from_answer_entries(answer_entries),
        extracted_meta?.legal_representation,
        extracted_meta?.applicants_legal_representatives,
        extracted_meta?.respondents_legal_representatives,
        saved_meta?.legal_representation,
        saved_meta?.applicants_legal_representatives,
        saved_meta?.respondents_legal_representatives,
        case_record?.legal_representation,
        case_record?.applicants_legal_representatives,
        case_record?.respondents_legal_representatives,
      ];
    },

    resolve_backend_case_status(case_record = {}) {
      const explicit_status = this.first_non_empty_value([
        case_record?.application_status,
        case_record?.status?.status,
        case_record?.status?.name,
      ]);

      if (explicit_status) {
        return explicit_status;
      }

      if (case_record?.workflow_complete === true) {
        return 'Complete';
      }

      if (case_record?.workflow_complete === false) {
        return 'In progress';
      }

      return '';
    },

    first_non_empty_value(values = []) {
      return values.find((value) => normalize_text(value)) || '';
    },

    resolve_case_status_label(backend_status, saved_status) {
      const normalized_backend_status = normalize_text(backend_status);
      if (normalized_backend_status && !is_generic_progress_status(normalized_backend_status)) {
        return normalized_backend_status;
      }

      const normalized_saved_status = normalize_text(saved_status);
      if (normalized_saved_status && !is_generic_progress_status(normalized_saved_status)) {
        return normalized_saved_status;
      }

      return this.first_non_empty_value([
        this.initial_workflow_status_label,
        normalized_saved_status,
        normalized_backend_status,
        'New case',
      ]);
    },
    open_load_case_modal() {
      this.submit_case_error = '';
      this.show_load_case_modal = true;
      this.hydrate_filter_catalogs().catch(handle_background_enrichment_error);
      this.sync_modal_query(true);
    },

    open_case_details(case_item) {
      if (!case_item?.application_id) {
        return;
      }

      this.$router.push({
        name: 'ViewCaseDetails',
        params: {
          applicationId: String(case_item.application_id),
        },
        query: {
          ...(this.case_context.project_id ? { project_id: String(this.case_context.project_id) } : {}),
        },
      });
    },

    toggle_filters() {
      this.show_filters = !this.show_filters;
      if (this.show_filters) {
        this.$nextTick(() => {
          this.load_google_map_event();
        });
        this.hydrate_filter_catalogs().catch(handle_background_enrichment_error);
      }
    },

    clear_filters() {
      this.filter_form = default_filter_form();
      this.apply_filters();
    },

    apply_filters() {
      this.current_page = 1;
    },

    load_google_map_event() {
      if (!GOOGLE_MAP_API_KEY || typeof window === 'undefined' || !this.show_filters) {
        return;
      }

      if (window.google?.maps?.places) {
        this.$nextTick(() => this.initialize_town_district_map());
        return;
      }

      window.initLandCourtFiltersMap = () => {
        this.$nextTick(() => this.initialize_town_district_map());
      };

      const existing_script = document.getElementById('googleMapsScript');
      if (existing_script) {
        existing_script.addEventListener(
          'load',
          () => this.$nextTick(() => this.initialize_town_district_map()),
          { once: true }
        );
        return;
      }

      const script = document.createElement('script');
      script.id = 'googleMapsScript';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAP_API_KEY}&libraries=places&callback=initLandCourtFiltersMap&v=weekly`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    },

    initialize_town_district_map() {
      const input = document.getElementById('land_court_town_district_filter');
      if (!input || !window.google?.maps?.places) {
        return;
      }

      if (this.town_district_autocomplete && this.town_district_input === input) {
        return;
      }

      this.town_district_autocomplete = new window.google.maps.places.Autocomplete(input, {
        componentRestrictions: { country: 'za' },
        fields: ['address_components', 'formatted_address', 'geometry', 'name'],
      });
      this.town_district_input = input;

      this.town_district_autocomplete.addListener('place_changed', () => {
        const place = this.town_district_autocomplete.getPlace();
        if (!place) {
          return;
        }

        const town_district = this.extract_town_district_from_place(place);
        const municipality = this.extract_municipality_from_place(place);

        this.filter_form = {
          ...this.filter_form,
          town_district: town_district || place.name || input.value || '',
          municipality: municipality || this.filter_form.municipality || '',
        };
        this.apply_filters();
      });
    },

    extract_town_district_from_place(place) {
      const components = place?.address_components || [];
      const find_component = (types) =>
        components.find((component) =>
          types.some((type) => component.types.includes(type))
        )?.long_name || '';

      return (
        find_component(['locality'])
        || find_component(['sublocality'])
        || find_component(['administrative_area_level_3'])
        || find_component(['administrative_area_level_2'])
        || place?.name
        || ''
      );
    },

    extract_municipality_from_place(place) {
      const components = place?.address_components || [];
      const find_component = (types) =>
        components.find((component) =>
          types.some((type) => component.types.includes(type))
        )?.long_name || '';

      return (
        find_component(['administrative_area_level_2'])
        || find_component(['administrative_area_level_3'])
        || find_component(['locality'])
        || ''
      );
    },

    go_to_page(page_number) {
      const normalized_page = Number(page_number || 1);
      if (normalized_page < 1 || normalized_page > this.total_group_pages || normalized_page === this.current_page) {
        return;
      }

      this.current_page = normalized_page;
    },

    async go_to_source_page(page_number) {
      const normalized_page = Number(page_number || 1);
      if (
        normalized_page < 1
        || normalized_page > this.source_total_pages
        || normalized_page === this.source_page
      ) {
        return;
      }

      this.source_page = normalized_page;
      this.current_page = 1;
      await this.load_caseload();
    },

    async handle_entity_type_selection_change() {
      this.submit_case_error = '';
      await this.refresh_case_context_for_selected_entity_type(this.selected_entity_type_id);
    },

    async on_load_case_entity_type_change(entity_type_id) {
      this.selected_entity_type_id = entity_type_id;
      await this.handle_entity_type_selection_change();
    },

    close_load_case_modal() {
      this.show_load_case_modal = false;
      this.submit_case_error = '';
      this.sync_modal_query(false);
    },

    sync_modal_query(should_open) {
      const next_query = { ...(this.$route?.query || {}) };
      delete next_query.entityTypeId;
      delete next_query.entity_type_id;

      if (should_open) {
        next_query.open_modal = LOAD_CASE_MODAL_QUERY;
      } else {
        delete next_query.open_modal;
      }

      this.$router.replace({
        name: 'LandCourtHome',
        query: next_query,
      });
    },

    strip_entity_type_query_params() {
      const next_query = { ...(this.$route?.query || {}) };
      const had_entity_type_query = Boolean(next_query.entityTypeId || next_query.entity_type_id);
      delete next_query.entityTypeId;
      delete next_query.entity_type_id;

      if (!had_entity_type_query) {
        return;
      }

      this.$router.replace({
        name: 'LandCourtHome',
        query: next_query,
      });
    },

    answer_text_from_entries(answer_entries = [], labels = []) {
      const normalized_labels = labels.map(normalize_label);
      const matching_entry = answer_entries.find((entry) => {
        const label = normalize_label(entry?.question_text);
        return normalized_labels.some((candidate) => label.includes(candidate));
      });

      return normalize_text(matching_entry?.answer_text);
    },

    representative_answer_text_from_entries(answer_entries = [], party_labels = []) {
      const normalized_party_labels = party_labels.map(normalize_label);
      const matching_entry = answer_entries.find((entry) => {
        const label = normalize_label(entry?.question_text);
        return is_representative_answer_label(label)
          && normalized_party_labels.some((candidate) => label.includes(candidate));
      });

      return normalize_text(matching_entry?.answer_text);
    },

    extract_case_meta_from_answers(answer_entries = []) {
      const forum = this.answer_text_from_entries(answer_entries, ['forum', 'court', 'location']);
      const legal_representation = representative_names_from_values(
        representative_values_from_answer_entries(answer_entries)
      ).join('\n\n');
      return {
        case_number: this.answer_text_from_entries(answer_entries, ['case number', 'reference']),
        forum,
        case_citation: this.answer_text_from_entries(answer_entries, ['case citation', 'citation', 'case name', 'matter']),
        farm: this.answer_text_from_entries(answer_entries, ['farm', 'property', 'land']),
        claim_type: this.answer_text_from_entries(answer_entries, ['claim type', 'claim']),
        section_16_status: this.answer_text_from_entries(answer_entries, ['section 16', 's16']),
        applicants: this.answer_text_from_entries(answer_entries, ['applicants', 'applicant']),
        respondents: this.answer_text_from_entries(answer_entries, ['respondents', 'respondent']),
        applicants_legal_representatives: this.representative_answer_text_from_entries(answer_entries, ['applicant', 'applicants']),
        respondents_legal_representatives: this.representative_answer_text_from_entries(answer_entries, ['respondent', 'respondents', 'landowner', 'landowners']),
        relief_sought: this.answer_text_from_entries(answer_entries, ['relief sought', 'relief', 'summary of prayers']),
        legal_representation,
        town_district: this.answer_text_from_entries(answer_entries, ['town district', 'town', 'district']),
        municipality: this.answer_text_from_entries(answer_entries, ['municipality', 'local municipality', 'district municipality']),
        province: province_from_location(forum),
      };
    },

    build_case_entity_name(case_meta = {}) {
      const farm = normalize_text(case_meta?.farm);
      const case_number = normalize_text(case_meta?.case_number);

      if (farm && case_number) {
        return `${farm} - ${case_number}`;
      }

      return farm || case_number || `Land Court Case ${Date.now()}`;
    },

    flatten_application_form_questions(application_form = {}) {
      return (application_form?.form?.sections || []).flatMap((section) =>
        (section?.questions || []).map((question) => ({
          section_question_id: Number(question?.section_question_id || 0),
          question_id: Number(question?.id || question?.question_id || 0),
          question_text: normalize_text(question?.question_text),
        }))
      );
    },

    flatten_application_form_answers(application_form = {}) {
      return (application_form?.form?.sections || []).flatMap((section) =>
        (section?.questions || []).map((question) => {
          const raw_answer = question?.answer;
          const answer_text = Array.isArray(raw_answer)
            ? raw_answer.join(', ')
            : typeof raw_answer === 'object' && raw_answer !== null
              ? normalize_text(raw_answer?.file_name || raw_answer?.original_url || '')
              : normalize_text(raw_answer);

          return {
            question_id: Number(question?.id || question?.question_id || 0) || null,
            question_text: normalize_text(question?.question_text),
            answer_text,
          };
        })
      );
    },

    select_preferred_application_form(application_forms = []) {
      const preferred_form_ids = new Set((this.case_context.form_ids || []).map((form_id) => Number(form_id)));

      return application_forms.find((application_form) =>
        preferred_form_ids.has(Number(application_form?.form?.id || 0))
      ) || application_forms[0] || null;
    },

    async hydrate_case_answer_meta(case_items = []) {
      const answer_meta_entries = await run_in_batches(
        case_items,
        CASE_ENRICHMENT_BATCH_SIZE,
        async (case_item) => {
          const application_id = Number(case_item?.application_id || 0);
          if (!application_id) {
            return null;
          }

          try {
            const application_forms = await fetch_land_court_application_forms(application_id);
            const preferred_application_form = this.select_preferred_application_form(application_forms);
            if (!preferred_application_form) {
              return null;
            }

            const answer_entries = this.flatten_application_form_answers(preferred_application_form);
            const extracted_meta = this.extract_case_meta_from_answers(answer_entries);

            if (!Object.values(extracted_meta).some((value) => normalize_text(value))) {
              return null;
            }

            return {
              application_id,
              application_form_id: Number(preferred_application_form?.application_form_id || 0),
              form_id: Number(preferred_application_form?.form?.id || 0),
              extracted_meta,
            };
          } catch {
            return null;
          }
        }
      );

      let has_changes = false;
      const next_case_meta = {
        ...(this.case_meta_by_application_id || {}),
      };

      answer_meta_entries.filter(Boolean).forEach((entry) => {
        const current_meta = read_case_meta_entry(next_case_meta, entry.application_id);
        const non_empty_extracted_meta = non_empty_object_entries(entry.extracted_meta);
        const merged_meta = {
          ...current_meta,
          ...non_empty_extracted_meta,
          application_id: entry.application_id,
          application_form_id: entry.application_form_id || current_meta.application_form_id || 0,
          entity_type_id: current_meta.entity_type_id || this.case_context.entity_type_id,
          entity_type_name: current_meta.entity_type_name || this.case_context.entity_type_name,
        };

        if (JSON.stringify(current_meta) === JSON.stringify(merged_meta)) {
          return;
        }

        Object.assign(
          next_case_meta,
          write_case_meta_entry(next_case_meta, entry.application_id, merged_meta)
        );
        has_changes = true;
      });

      if (has_changes) {
        this.case_meta_by_application_id = next_case_meta;
        persist_case_meta(this.case_meta_by_application_id);
      }
    },

    build_answer_submission_payload(runtime_questions = [], submitted_answers = []) {
      const answers_by_section_question_id = new Map();
      const answers_by_question_id = new Map();
      const answers_by_question_text = new Map();

      submitted_answers.forEach((answer) => {
        const section_question_id = Number(answer?.section_question_id || 0);
        const question_id = Number(answer?.question_id || 0);
        const question_text = normalize_label(answer?.question_text);
        const answer_text = normalize_text(answer?.answer_text);

        if (section_question_id) {
          answers_by_section_question_id.set(section_question_id, answer_text);
        }
        if (question_id) {
          answers_by_question_id.set(question_id, answer_text);
        }
        if (question_text) {
          answers_by_question_text.set(question_text, answer_text);
        }
      });

      return runtime_questions
        .map((question) => {
          const answer_text = answers_by_section_question_id.get(question.section_question_id)
            || (question.question_id
              ? answers_by_question_id.get(question.question_id)
              : answers_by_question_text.get(normalize_label(question.question_text)));

          if (!question.section_question_id || !normalize_text(answer_text)) {
            return null;
          }

          return {
            section_question_id: question.section_question_id,
            answer_text,
          };
        })
        .filter(Boolean);
    },

    async get_runtime_application_form(application_id) {
      const application_forms = await fetch_land_court_application_forms(application_id);
      return this.select_preferred_application_form(application_forms);
    },

    async get_child_runtime_application_form(parent_application_id, child_entity_profile_id) {
      const application_forms = await fetch_land_court_application_forms(parent_application_id);
      const preferred_form_ids = new Set((this.case_context.form_ids || []).map((form_id) => Number(form_id)));
      const child_entity_id = Number(child_entity_profile_id || 0);

      return application_forms.find((application_form) =>
        preferred_form_ids.has(Number(application_form?.form?.id || 0))
        && Number(application_form?.entity_profile || 0) === child_entity_id
      ) || application_forms.find((application_form) =>
        Number(application_form?.entity_profile || 0) === child_entity_id
      ) || null;
    },

    async submit_load_case(form_submission) {
      this.submit_case_error = '';

      if (!this.case_context.entity_type_id) {
        this.submit_case_error = 'No backend entity type has been configured for land court cases yet.';
        this.show_error(this.submit_case_error);
        return;
      }

      if (!this.case_context.form_ids.length) {
        this.submit_case_error = 'No active form is currently configured for this case type.';
        this.show_error(this.submit_case_error);
        return;
      }

      this.create_case_running = true;
      const loader = await this.show_loader('Adding case to caseload...');

      try {
        const case_meta = this.extract_case_meta_from_answers(form_submission?.answers || []);
        const entity_name = this.build_case_entity_name(case_meta);
        let answer_submission_warning = '';
        const has_parent_context = Boolean(
          this.case_context.parent_application_id
          && this.case_context.parent_entity_profile_id
          && this.case_context.parent_entity_type_id
        );

        const response = await create_land_court_case({
          entity_name,
          entity_type_id: this.case_context.entity_type_id,
          ...(this.case_context.project_id ? { project_id: this.case_context.project_id } : {}),
          ...(this.case_context.form_ids.length ? { form_ids: this.case_context.form_ids } : {}),
          ...(has_parent_context ? {
            parent_application_id: this.case_context.parent_application_id,
            parent_entity_profile_id: this.case_context.parent_entity_profile_id,
            parent_entity_type_id: this.case_context.parent_entity_type_id,
          } : {}),
        });

        if (String(response?.status || '').toLowerCase() === 'error') {
          const backend_message =
            response?.message
            || response?.errors?.entity_type_id?.[0]
            || 'Failed to add the case to the caseload.';
          throw new Error(backend_message);
        }

        const application_id = Number(
          response?.application_id
          || response?.data?.application_id
          || this.case_context.parent_application_id
          || 0
        );
        const child_entity_profile_id = Number(
          response?.entity_profile_id
          || response?.data?.entity_profile_id
          || 0
        );

        if (!application_id) {
          throw new Error('The backend did not return a usable application id for the loaded case.');
        }

        const runtime_application_form = has_parent_context
          ? await this.get_child_runtime_application_form(application_id, child_entity_profile_id)
          : await this.get_runtime_application_form(application_id);
        if (!runtime_application_form?.application_form_id) {
          throw new Error('The backend created the case, but no application form was returned for answer submission.');
        }

        const runtime_form_id = Number(runtime_application_form?.form?.id || 0);
        let initial_workflow_status = null;
        if (runtime_form_id) {
          try {
            const workflow_status_response = await fetch_assign_form_status_data({ form_id: runtime_form_id });
            const assigned_statuses = workflow_status_response?.data?.assigned_statuses
              || workflow_status_response?.assigned_statuses
              || [];
            initial_workflow_status = assigned_statuses.find((status) => Boolean(status?.is_initial))
              || assigned_statuses[0]
              || null;
          } catch {
            initial_workflow_status = null;
          }
        }

        const runtime_questions = this.flatten_application_form_questions(runtime_application_form);
        const answers_payload = this.build_answer_submission_payload(
          runtime_questions,
          form_submission?.answers || []
        );

        if (answers_payload.length) {
          const section_count = Array.isArray(runtime_application_form?.form?.sections)
            ? runtime_application_form.form.sections.length
            : 0;

          try {
            await submit_land_court_form_answers({
              application_form_id: Number(runtime_application_form.application_form_id),
              current_section_index: Math.max(section_count - 1, 0),
              answers: answers_payload,
            });
          } catch (answer_error) {
            answer_submission_warning =
              answer_error?.response?.data?.message
              || answer_error?.message
              || 'The case was created, but some form answers could not be saved.';
          }
        }

        if (application_id > 0) {
          this.case_meta_by_application_id = {
            ...(this.case_meta_by_application_id || {}),
            [String(application_id)]: {
              ...case_meta,
              application_id,
              application_form_id: Number(runtime_application_form.application_form_id || 0),
              entity_name,
              entity_profile_id: child_entity_profile_id || Number(response?.entity_profile_id || 0),
              entity_type_id: this.case_context.entity_type_id,
              entity_type_name: this.case_context.entity_type_name,
              status: initial_workflow_status?.status_name || 'New case',
              workflow_form_status_id: Number(initial_workflow_status?.id || 0) || null,
              workflow_status_id: Number(initial_workflow_status?.status_id || 0) || null,
              parent_application_id: this.case_context.parent_application_id,
              parent_entity_profile_id: this.case_context.parent_entity_profile_id,
              parent_entity_type_id: this.case_context.parent_entity_type_id,
            }
          };
          persist_case_meta(this.case_meta_by_application_id);
        }

        await this.load_caseload();
        this.close_load_case_modal();
        if (answer_submission_warning) {
          this.show_error(answer_submission_warning);
        } else {
          this.show_success('Case added to caseload.');
        }
      } catch (error) {
        const message =
          error?.response?.data?.message
          || error?.response?.data?.data?.message
          || error?.response?.data?.errors?.entity_type_id?.[0]
          || error?.message
          || 'Failed to add the case to the caseload.';
        this.submit_case_error = message;
        this.show_error(message);
      } finally {
        this.create_case_running = false;
        await this.close_loader(loader);
      }
    },

    is_group_open(group_id) {
      return this.open_group_ids.includes(group_id);
    },

    toggle_group(group_id) {
      if (this.is_group_open(group_id)) {
        this.open_group_ids = this.open_group_ids.filter((id) => id !== group_id);
        return;
      }

      this.open_group_ids = [...this.open_group_ids, group_id];
    },

    status_variant_from_status(status_label) {
      const normalized_status = String(status_label || '').toLowerCase();

      if (normalized_status.includes('hear')) {
        return 'hearing';
      }

      if (
        normalized_status.includes('stuck')
        || normalized_status.includes('overdue')
        || normalized_status.includes('late')
      ) {
        return 'warning';
      }

      return 'success';
    },

    status_badge_class(status_variant) {
      const badge_classes = {
        warning: 'land-court-status-warning',
        success: 'land-court-status-success',
        hearing: 'land-court-status-hearing'
      };

      return badge_classes[status_variant] || 'bg-light text-dark';
    }
  }
};

export default LandCourtHome;

function extract_primary_party_name(value) {
  const normalized = normalize_text(value);
  if (!normalized) {
    return '';
  }

  return normalized
    .split(/\r?\n|,|;/)
    .map((segment) => normalize_text(segment))
    .find(Boolean) || '';
}

function build_party_case_name(applicants = '', respondents = '') {
  const applicant_name = extract_primary_party_name(applicants);
  const respondent_name = extract_primary_party_name(respondents);

  if (applicant_name && respondent_name) {
    return `${applicant_name} vs ${respondent_name}`;
  }

  return applicant_name || respondent_name || '';
}
